import { Injectable } from '@angular/core';
import { DatePipe } from '@angular/common';
import * as moment from 'moment';
import { Moment } from 'moment';

import { EDatePresets } from '../consts/enums';
import { DateRange, timeStamp, timeString } from '../models/interfaces';
import { NBSP } from '../consts/constants';
import { Timestamp } from 'rxjs/internal/operators/timestamp';

@Injectable({
  providedIn: 'root',
})
export class DateTimeService {
  constructor(private datePipe: DatePipe) {}

  public UTCDate = (date: Date | string): string => {
    const _date = new Date(date);
    _date.setMinutes(_date.getMinutes() - _date.getTimezoneOffset());
    return _date.toISOString();
  };

  public UTCTime = (date: Date | string): string => {
    return this.datePipe.transform(date, 'HH:mm', '+0000');
  };

  public convert12To24Hours = (time: string): string => {
    const is12Format = time.split(' ').length > 1;
    if (!is12Format) {
      return time;
    }
    const parsedTime = moment(time, 'h:mm a');
    return parsedTime.format('HH:mm');
  };

  public isValid12HourTime(timeStr: string): boolean {
    // Regex: hours (1-12), minutes (00-59), optional space, AM/PM (case insensitive)
    const re = /^(0?[1-9]|1[0-2]):([0-5][0-9])\s?([AaPp][Mm])$/;
    return re.test(timeStr);
  }
  public convert12To24HoursAMPM = (time: string): string => {
    const is12Format = time.split(' ').length > 1;
    if (!is12Format) {
      return moment(time, 'h:mm a').format('HH:mm A');
    }
    const parsedTime = moment(time, 'h:mm a');
    return parsedTime.format('HH:mm A');
  };

  public normalizeTimeString = (time: string): string => {
    if (!time) return '';
    // If already in HH:mm, return as is
    if (/^\d{1,2}:\d{2}$/.test(time)) return time;
    // If in HH:mm:ss, trim to HH:mm
    if (/^\d{1,2}:\d{2}:\d{2}$/.test(time)) return time.slice(0, 5);
    // If in 12-hour format, convert to 24-hour
    return this.convert12To24Hours(time.trim());
  };

  public extractTime(time: string): string {
    if (!time) return '';
    const timeOnly24h = /^(?:[01]\d|2[0-3]):[0-5]\d(?::[0-5]\d)?$/;

    const timeOnly12h = /^(0?[1-9]|1[0-2]):[0-5][0-9]\s?(?:[AaPp][Mm])$/;

    if (timeOnly24h.test(time) || timeOnly12h.test(time)) {
      return time;
    }

    const date = new Date(time);
    const hh = String(date.getUTCHours()).padStart(2, '0');
    const mm = String(date.getUTCMinutes()).padStart(2, '0');
    return `${hh}:${mm}`;
  }

  public getToday = (): Moment => {
    return moment().utc(true).startOf('day');
  };

  public getDateRange = (
    preset: EDatePresets,
    referenceDate?: Moment
  ): DateRange => {
    const startDate = (referenceDate || moment()).clone().utc(true);
    const endDate = (referenceDate || moment()).clone().utc(true);

    const dateRange: DateRange = {
      fromDate: null,
      toDate: null,
    };

    const presetActions: Record<EDatePresets, () => void> = {
      [EDatePresets.Today]: () => {
        dateRange.fromDate = startDate.startOf('day');
        dateRange.toDate = endDate.endOf('day');
      },
      [EDatePresets.Yesterday]: () => {
        dateRange.fromDate = startDate.subtract(1, 'day').startOf('day');
        dateRange.toDate = endDate.subtract(1, 'day').endOf('day');
      },
      [EDatePresets.Tomorrow]: () => {
        dateRange.fromDate = startDate.add(1, 'day').startOf('day');
        dateRange.toDate = endDate.add(1, 'day').endOf('day');
      },
      [EDatePresets.ThisWeek]: () => {
        dateRange.fromDate = startDate.startOf('week');
        dateRange.toDate = endDate.endOf('week');
      },
      [EDatePresets.LastWeek]: () => {
        dateRange.fromDate = startDate.subtract(1, 'week').startOf('week');
        dateRange.toDate = endDate.subtract(1, 'week').endOf('week');
      },
      [EDatePresets.NextWeek]: () => {
        dateRange.fromDate = startDate.add(1, 'week').startOf('week');
        dateRange.toDate = endDate.add(1, 'week').endOf('week');
      },
      [EDatePresets.LastSevenDays]: () => {
        dateRange.fromDate = startDate.subtract(6, 'day').startOf('day');
        dateRange.toDate = endDate.endOf('day');
      },
      [EDatePresets.LastThirtyDays]: () => {
        dateRange.fromDate = startDate.subtract(29, 'day').startOf('day');
        dateRange.toDate = endDate.endOf('day');
      },
      [EDatePresets.ThisMonth]: () => {
        dateRange.fromDate = startDate.startOf('month');
        dateRange.toDate = endDate.endOf('month');
      },
      [EDatePresets.PreviousMonth]: () => {
        dateRange.fromDate = startDate.subtract(1, 'month').startOf('month');
        dateRange.toDate = endDate.subtract(1, 'month').endOf('month');
      },
      [EDatePresets.NextMonth]: () => {
        dateRange.fromDate = startDate.add(1, 'month').startOf('month');
        dateRange.toDate = endDate.add(1, 'month').endOf('month');
      },
    };
    presetActions[preset]();

    return dateRange;
  };

  public isEqualDates = (a, b) => {
    const aDate = moment(a).startOf('day');
    const bDate = moment(b).startOf('day');
    return aDate.isSame(bDate);
  };

  public toDateObject = (dateString: string): Moment => {
    return moment(dateString, 'YYYY-MM-DD');
  };

  public fromDateObject = (dateObject: Moment, utcFormat = false): string => {
    return utcFormat
      ? dateObject.toISOString()
      : dateObject.format('YYYY-MM-DD');
  };

  public fromTimeStampToReadableDate = (dateObject: timeStamp): string => {
    const date = moment(dateObject).format('YYYY-MM-DD');
    return date;
  };

  public toDateTimeObject = (dateTimeString: timeStamp): Moment => {
    return moment(dateTimeString, 'YYYY-MM-DDTHH:mm:ss.SSSZ');
  };

  public fromDateTimeObject = (dateObject: Moment): string => {
    return dateObject.format('YYYY-MM-DDTHH:mm:ss.SSSZ');
  };

  public dateStrToTimeStr = (dateTimeString: timeStamp): timeString => {
    return moment(dateTimeString).utc().toISOString().slice(11, 16);
  };

  public setTimeToDate = (time: timeString, date: Moment): Moment => {
    const newDate = date.clone();
    const timeArr: number[] = time.split(':').map(value => Number(value));
    newDate.set('hours', timeArr[0]);
    newDate.set('minutes', timeArr[1]);
    newDate.set('seconds', 0);
    newDate.set('milliseconds', 0);
    return newDate;
  };

  public addMinutesToTimeString = (
    minutes: number,
    time: timeString
  ): timeString => {
    const dateObj = moment(time, 'HH:mm');
    dateObj.add(minutes, 'minutes');
    return dateObj.format('HH:mm');
  };

  public minutesToString = (minutes: number): timeString => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;

    const paddedHours = String(hours).padStart(2, '0');
    const paddedMinutes = String(mins).padStart(2, '0');

    return `${paddedHours}:${paddedMinutes}`;
  };

  public getTimeRangeInMinutes = (
    prevTimeStr: timeString,
    curTimeStr: timeString
  ): number => {
    const timeArr = curTimeStr.split(':').map(value => Number(value));
    const startTimeArr = prevTimeStr.split(':').map(value => Number(value));
    const result =
      timeArr[0] * 60 + timeArr[1] - (startTimeArr[0] * 60 + startTimeArr[1]);
    return result >= 0 ? result : 24 * 60 + result;
  };

  public getRangeTimeInMinutes = (
    startTime: timeString,
    endTime: timeStamp
  ): number => {
    const startTimeArr = startTime.split(':');
    const startTimeInMinutes =
      Number(startTimeArr[0]) * 60 +
      Number(startTimeArr[1]) +
      (Number(startTimeArr[2]) ? Number(startTimeArr[2]) / 60 : 0);

    const endTimeArr = endTime.split('T')[1].split(':');
    const endTimeInMinutes =
      Number(endTimeArr[0]) * 60 +
      Number(endTimeArr[1]) +
      Number(endTimeArr[2].substring(0, 2)) / 60;

    return endTimeInMinutes - startTimeInMinutes;
  };

  public getRangeHoursArray = (
    startHour: timeString,
    endHour: timeString
  ): timeString[] => {
    const start: number = +startHour.split(':')[0];
    const end: number = +endHour.split(':')[0];
    const length = end - start + 1;

    return Array.from({ length }, (v, i) => {
      const hour = start + i;
      return `${hour >= 10 ? hour : '0' + hour}:00`;
    });
  };

  public getHoursAndMinutesFromSeconds = (seconds: number): string => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const minutesStr = minutes;
    const result = [hours ? `${hours} h` : '', `${minutesStr} m`];
    return result.join(NBSP);
  };

  public roundFloorTime = (time: timeStamp): timeString => {
    return `${time.split('T')[1].split(':')[0]}:00`;
  };

  public isToday = (dateString: timeStamp): boolean => {
    return this.toDateTimeObject(dateString)
      .utc(true)
      .startOf('day')
      .isSame(this.getToday());
  };

  public getTimeSlots = (hour: timeString, interval: number): timeString[] => {
    const [hours, minutes] = hour.split(':').map(Number);

    const startTime = new Date();
    startTime.setHours(hours, minutes, 0, 0);

    const endTime = new Date(startTime);
    endTime.setHours(hours + 1, 0, 0, 0);

    return Array.from(
      {
        length:
          Math.ceil(
            (endTime.getTime() - startTime.getTime()) / (interval * 60 * 1000)
          ) + 1,
      },
      (_, index) => {
        const currentTime = new Date(
          startTime.getTime() + index * interval * 60 * 1000
        );
        return currentTime.toTimeString().slice(0, 5);
      }
    );
  };

  public normalize12HourTime(timeStr: string): string | null {
    timeStr = timeStr.trim();

    // Regex: hours (1-12 with optional leading 0), minutes (00-59), optional space + optional AM/PM
    const re = /^(0?[1-9]|1[0-2]):([0-5][0-9])(?:\s?([AaPp][Mm]))?$/;
    const m = re.exec(timeStr);
    if (!m) {
      return null; // invalid format
    }

    const hour = m[1];
    const minute = m[2];
    const meridiem = m[3] ? m[3].toUpperCase() : 'AM'; // default to AM if missing

    return `${hour}:${minute} ${meridiem}`;
  }

  public normalizeTime(input: string) {
    const m = input.trim().match(/^(\d{1,2}):(\d{2})(?:\s*([AaPp][Mm]))?$/);
    if (!m) {
      throw new Error('Invalid time format: ' + input);
    }
    let hour = parseInt(m[1], 10);
    const minute = m[2];
    let ampm = m[3] ? m[3].toLowerCase() : null;

    if (ampm && hour > 12) {
      hour = hour - 12;
    }

    if (!ampm) {
      ampm = hour >= 12 ? 'PM' : 'AM';
      hour = hour % 12;
      if (hour === 0) hour = 12;
    }

    return `${hour}:${minute} ${ampm}`;
  }

  public convertDateTimeToTimeAMPM(strDate) {
    const date = new Date(strDate);
    const isDate = !isNaN(date.valueOf());
    //if not date, then it's tim already, return as is
    if (!isDate) {
      return this.addAmPmIfMissing(strDate);
    }

    let hours = date.getUTCHours();
    const minutes = date.getUTCMinutes();
    const ampm = hours >= 12 ? 'PM' : 'AM';

    hours = hours % 12;
    hours = hours ? hours : 12;

    const minsStr = minutes < 10 ? '0' + minutes : minutes;

    return hours + ':' + minsStr + ' ' + ampm;
  }

  private addAmPmIfMissing(timeStr) {
    // Trim whitespace
    const str = timeStr.trim();

    // Check if it already has am/pm (case-insensitive)
    const hasMeridiem = /(?:\s*)(am|pm)$/i.test(str);

    if (hasMeridiem) {
      // Already has AM or PM, just normalize it (uppercase)
      return str.replace(/(am|pm)$/i, match => match.toUpperCase());
    }

    // No AM/PM — decide based on hour (assume 24h time)
    // Extract hours and minutes
    const m = str.match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/);
    if (!m) {
      // Not a simple hh:mm or hh:mm:ss format — just append default
      return str + ' AM';
    }

    let hour = +m[1];
    const minute = m[2];

    // Guess AM/PM: hour >= 12 → PM, otherwise AM
    const meridiem = hour >= 12 ? 'PM' : 'AM';

    // Convert hour to 12-hour clock
    hour = hour % 12;
    if (hour === 0) hour = 12;

    // Return normalized time
    return `${hour}:${minute} ${meridiem}`;
  }

  public timeToDate(date: Date, time12h: string): Date | null {
    // Parse string e.g. "6:20 AM" or "11:45 PM"
    const m = time12h.trim().match(/^(\d{1,2}):([0-5][0-9])\s*([AaPp][Mm])$/);
    if (!m) return null;

    let hour = parseInt(m[1], 10);
    const minute = parseInt(m[2], 10);
    const meridiem = m[3].toUpperCase();

    if (meridiem === 'AM') {
      if (hour === 12) hour = 0; // 12 AM → 0 hours
    } else {
      // "PM"
      if (hour < 12) hour += 12; // 1-11 PM → 13-23
    }

    const dt = new Date(date); // copy so as not to mutate original
    dt.setHours(hour, minute, 0, 0);
    return dt;
  }

  public addMinutesToTimeStringWithAMPM = (
    minutes: number,
    time: string
  ): string => {
    const dateObj = moment(time, ['HH:mm', 'hh:mm A']);
    if (!dateObj.isValid()) return '';
    dateObj.add(minutes, 'minutes');
    return dateObj.format('hh:mm A');
  };
}
